// src/paginas/publico/cadastro/cadastro.tsx
import React, { ForwardedRef, useState } from 'react';
import {
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Box,
  Stack,
  useTheme
} from '@mui/material';
import { CheckCircleOutline, ArrowForwardIos } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import ServiceAPI from '../../services/serviceapi';
import { FormatUtils } from '../../utils/formatutils';
import { PatternUtils } from '../../utils/patterutils';

interface CadastroData {
  nome?: string;
  email?: string;
  url?: string;
  cpfCnpj?: string;
  celular?: string;
  cep?: string;
}

const CORES = {
  verde: '#82b07d',
  marinho: '#1d2b4b',
  fundo: '#f8fafc'
};

const Cadastro = React.forwardRef((_props: unknown, _ref: ForwardedRef<any>) => {
  const navigate = useNavigate();
  const [cadastro, setCadastro] = useState<CadastroData>({ url: '' });

  const botaoDesativado = (): boolean => {
    return !cadastro.nome || !cadastro.email || !cadastro.url || !cadastro.cpfCnpj;
  };

  const cadastrar = async () => {
    try {
      const result = await ServiceAPI.consumeService({
        url: '/public/cadastro/cliente',
        data: cadastro,
        config: { ignoreToast: false }
      });

      if (result.codeResponse === 'SUCESSO_PERSISTENCIA') {
        toast.success('Sucesso! Confira seu e-mail.');
        navigate(`/confirmacaocadastro`);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Falha ao cadastrar');
    }
  };

  const onChangeUrl = (value: string) => {
    const cleanValue = value.replace(/[^A-Za-z0-9]/g, '').toLowerCase().trim();
    setCadastro({ ...cadastro, url: cleanValue });
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: CORES.fundo, 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center',
      py: 4,
    }}>
      {/* Container mais estreito (xs em vez de sm) para campos menores */}
      <Container maxWidth="xs"> 
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {/* Logo com container de altura controlada */}
          <Box sx={{ height: 60, display: 'flex', justifyContent: 'center', mb: 1 }}>
            <img 
              src="/AppCodesh.png" 
              alt="App Codesh" 
              style={{ 
                maxHeight: '100%', 
                maxWidth: '100%', 
                objectFit: 'contain',
                cursor: 'pointer' 
              }} 
              onClick={() => navigate('/')}
            />
          </Box>
          <Typography variant="h5" fontWeight={900} sx={{ color: CORES.marinho }}>
            Vamos começar!
          </Typography>
        </Box>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            borderRadius: '24px', 
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' 
          }}
        >
          {/* Spacing reduzido para 1.5 para o formulário ficar mais compacto */}
          <Stack spacing={1.5}>
            <TextField
              label="Nome Completo"
              size="small"
              value={cadastro.nome || ''}
              onChange={(e) => setCadastro({ ...cadastro, nome: e.target.value })}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            <TextField
              label="CPF ou CNPJ"
              size="small"
              value={cadastro.cpfCnpj || ''}
              onChange={(e) => setCadastro({ ...cadastro, cpfCnpj: FormatUtils.formatarCpfCnpj(e.target.value) })}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            <TextField
              label="WhatsApp"
              size="small"
              value={cadastro.celular || ''}
              onChange={(e) => setCadastro({ ...cadastro, celular: FormatUtils.formatarNumeroCelular(e.target.value) })}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            <TextField
              label="Email"
              size="small"
              type="email"
              value={cadastro.email || ''}
              onChange={(e) => setCadastro({ ...cadastro, email: e.target.value })}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            <TextField
              label="ID da Empresa"
              size="small"
              value={cadastro.url || ''}
              onChange={(e) => onChangeUrl(e.target.value)}
              fullWidth
              helperText="Ex: minha-empresa"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            <TextField
              label="CEP"
              size="small"
              value={cadastro.cep || ''}
              onChange={(e) => setCadastro({ ...cadastro, cep: FormatUtils.formatarCEP(e.target.value) })}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            <Button
              variant="contained"
              disabled={botaoDesativado()}
              onClick={cadastrar}
              fullWidth
              endIcon={<ArrowForwardIos sx={{ fontSize: 12 }} />}
              sx={{
                py: 1.5,
                mt: 1,
                borderRadius: '10px',
                fontWeight: 700,
                textTransform: 'none',
                bgcolor: CORES.verde,
                '&:hover': { bgcolor: '#6d9668' },
              }}
            >
              Próximo
            </Button>
          </Stack>
        </Paper>

        <Typography variant="body2" textAlign="center" sx={{ mt: 3, color: '#64748b' }}>
          Já tem conta?{' '}
          <span 
            onClick={() => navigate('/login')}
            style={{ color: CORES.marinho, fontWeight: 700, cursor: 'pointer' }}
          >
            Login
          </span>
        </Typography>
      </Container>
    </Box>
  );
});

export default Cadastro;